import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { DocumentLevel, LandUsePlanDocument } from '@popety-io/types'
import { DocumentsList } from './DocumentsList'
import { Text } from '../../../../atoms/Text'
import { ExpandArrow } from '../../../../atoms/ExpandArrow'
import { Loading } from '../../../../atoms/Loading'

const GroupContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  overflow: hidden;
`

const GroupHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.default};
  background-color: ${({ theme, $isExpanded }) =>
    $isExpanded
      ? theme.colors.background.secondary
      : theme.colors.background.primary};
  border: none;
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

const HeaderTitle = styled(Text)`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`

const DocumentCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 ${({ theme }) => theme.spacing.xsmall};
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background-color: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`

const GroupContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${({ $isExpanded }) => ($isExpanded ? '2000px' : '0')};
  opacity: ${({ $isExpanded }) => ($isExpanded ? '1' : '0')};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.medium};
  padding: ${({ $isExpanded, theme }) =>
    $isExpanded ? theme.spacing.default : '0'};
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.large};
`

const ErrorMessage = styled(Text)`
  color: ${({ theme }) => theme.colors.danger};
  padding: ${({ theme }) => theme.spacing.default};
  text-align: center;
`

interface DocumentsGroupProps {
  level: DocumentLevel
  title: string
  documents?: LandUsePlanDocument[]
  isLoading?: boolean
  error?: string
  defaultExpanded?: boolean
  onExpand?: () => void
}

export const DocumentsGroup: React.FC<DocumentsGroupProps> = ({
  level,
  title,
  documents,
  isLoading = false,
  error,
  defaultExpanded = false,
  onExpand,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const handleToggle = () => {
    const newExpandedState = !isExpanded
    setIsExpanded(newExpandedState)

    // Call onExpand when expanding (for lazy loading)
    if (newExpandedState && onExpand) {
      onExpand()
    }
  }

  useEffect(() => {
    setIsExpanded(defaultExpanded)
  }, [defaultExpanded])

  const documentCount = documents?.length || 0

  return (
    <GroupContainer>
      <GroupHeader
        onClick={handleToggle}
        $isExpanded={isExpanded}
        type="button"
      >
        <HeaderLeft>
          <HeaderTitle>{title}</HeaderTitle>
          {documentCount > 0 && <DocumentCount>{documentCount}</DocumentCount>}
        </HeaderLeft>
        <ExpandArrow $expanded={isExpanded} direction="down" />
      </GroupHeader>

      <GroupContent $isExpanded={isExpanded}>
        {isLoading ? (
          <LoadingContainer>
            <Loading size="small" />
          </LoadingContainer>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <DocumentsList
            documents={documents || []}
            emptyMessage={`No ${level} documents available`}
          />
        )}
      </GroupContent>
    </GroupContainer>
  )
}
